import { Trans } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { TriangleAlert } from "lucide-react";

import { LinkBuilder } from "@/components/admin/marketing/links/link-builder";
import { LINKS_QUERY_PREFIX, LinksTable } from "@/components/admin/marketing/links/links-table";
import { normalizeConventions } from "@/components/admin/marketing/links/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Marketing → Links — governed campaign links.
 *
 * A UTM set is validated against the naming convention ONCE, here, and every
 * channel (social, email, SMS, ads, print) uses the resulting short link.
 * Free-typed UTMs are how attribution rots. Spec:
 * `_docs/marketing-automation-spec.md` §4 (the convention) and §3 WP-B.
 *
 *   GET  /api/admin/marketing/links/conventions   — drives every select + regex check
 *   GET  /api/admin/marketing/links?q=&limit=&offset=
 *   POST /api/admin/marketing/links               — 422 convention_violation → field error
 *
 * Search and paging are URL state (`?q=&offset=`), so a filtered list is
 * linkable. Queries carry `dbTarget` in their keys (Local↔Prod admin toggle).
 */

interface LinksSearch {
  q?: string;
  offset?: number;
}

function asOffset(value: unknown): number | undefined {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
}

export const Route = createFileRoute("/_authenticated/marketing-links")({
  validateSearch: (search: Record<string, unknown>): LinksSearch => ({
    q:
      typeof search.q === "string" && search.q.trim() !== ""
        ? search.q.trim().slice(0, 100)
        : undefined,
    offset: asOffset(search.offset),
  }),
  component: MarketingLinksPage,
});

function MarketingLinksPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { dbTarget } = useAdminContext();

  const patchSearch = (next: Partial<LinksSearch>) => {
    void navigate({
      search: (prev: LinksSearch) => ({ ...prev, ...next }),
      replace: true,
    });
  };

  const conventionsQuery = useQuery({
    queryKey: [...LINKS_QUERY_PREFIX, "conventions", dbTarget],
    queryFn: async () =>
      normalizeConventions(await apiFetch.get<unknown>("/api/admin/marketing/links/conventions")),
    staleTime: 5 * 60_000,
  });

  const conventions = conventionsQuery.data;
  const conventionsUsable =
    !!conventions && conventions.sources.length > 0 && conventions.mediums.length > 0;

  const onCreated = () => {
    // Newest first — jump back to the first page so the new link is visible.
    patchSearch({ offset: undefined });
    void queryClient.invalidateQueries({ queryKey: [...LINKS_QUERY_PREFIX, "list"] });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Campaign links</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>
            Build UTM links that follow the naming convention, get a short link for every placement,
            and see the clicks each one earned.
          </Trans>
        </p>
      </header>

      {conventionsQuery.isError ? (
        <Card>
          <CardContent className="space-y-3 p-6 text-center text-sm">
            <p className="text-muted-foreground">
              {conventionsQuery.error instanceof ApiError ? (
                <Trans>
                  Could not load the link convention ({conventionsQuery.error.status}). The builder
                  needs it — it never guesses the rules.
                </Trans>
              ) : (
                <Trans>
                  Could not load the link convention. The builder needs it — it never guesses the
                  rules.
                </Trans>
              )}
            </p>
            <Button
              variant="outline"
              className="min-h-[44px] px-4"
              onClick={() => void conventionsQuery.refetch()}
            >
              <Trans>Try again</Trans>
            </Button>
          </CardContent>
        </Card>
      ) : conventionsQuery.isLoading ? (
        <Skeleton className="h-[34rem] w-full rounded-xl" />
      ) : !conventionsUsable ? (
        <div className="border-warning-border bg-warning-soft flex items-start gap-3 rounded-lg border p-4">
          <TriangleAlert className="text-warning-strong size-5 shrink-0" />
          <p className="text-warning-strong text-sm">
            <Trans>
              The API returned a link convention without sources or mediums, so no link can be built
              yet. Existing links are listed below.
            </Trans>
          </p>
        </div>
      ) : (
        conventions && <LinkBuilder conventions={conventions} onCreated={onCreated} />
      )}

      <LinksTable
        search={search.q ?? ""}
        offset={search.offset ?? 0}
        publicBaseUrl={conventions?.publicBaseUrl ?? null}
        onSearchChange={(q) => patchSearch({ q: q || undefined, offset: undefined })}
        onOffsetChange={(offset) => patchSearch({ offset: offset > 0 ? offset : undefined })}
      />
    </div>
  );
}
